import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type UserRef } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMonth, formatNumber } from '../lib/format';
import { FormPage } from '../components/FormPage';
import { Field, Input, Select, useToast } from '../components/ui';

/** `GET /users/:id/deletion`: what deleting them would take. */
interface DeletionPlan {
  employee: { id: string; fullName: string; fullNameAr: string | null; email: string; employeeCode: string | null };
  refusal: 'SELF' | 'LAST_SYS_ADMIN' | null;
  needsSuccessor: boolean;
  handOver: Record<string, number>;
  made: Record<string, number>;
  own: Record<string, number>;
  released: Record<string, number>;
  signedOffPayslips: Array<{ year: number; month: number; status: string }>;
}

/** Each group's lines, in the order shown; a line counting nothing is left out. */
const GROUPS: Array<{ key: 'made' | 'own' | 'handOver' | 'released'; title: TranslationKey; lines: Record<string, TranslationKey> }> = [
  {
    key: 'made',
    title: 'employees.deleteMade',
    lines: {
      tasks: 'employees.del.tasks',
      tickets: 'employees.del.tickets',
      qTasks: 'employees.del.qTasks',
      announcements: 'employees.del.announcements',
      mails: 'employees.del.mails',
      workflows: 'employees.del.workflows',
      meetings: 'employees.del.meetings',
      comments: 'employees.del.comments',
      checklistItems: 'employees.del.checklistItems',
    },
  },
  {
    key: 'own',
    title: 'employees.deleteOwn',
    lines: {
      attendanceDays: 'employees.del.attendanceDays',
      leaves: 'employees.del.leaves',
      rosterDays: 'employees.del.rosterDays',
      salaryRecords: 'employees.del.salaryRecords',
      payslips: 'employees.del.payslips',
      penalties: 'employees.del.penalties',
      violations: 'employees.del.violations',
      mailReceived: 'employees.del.mailReceived',
      chats: 'employees.del.chats',
      groupMessages: 'employees.del.groupMessages',
      notes: 'employees.del.notes',
      notifications: 'employees.del.notifications',
      walletRedemptions: 'employees.del.walletRedemptions',
    },
  },
  {
    key: 'handOver',
    title: 'employees.deleteHandOver',
    lines: {
      projects: 'employees.del.projects',
      contracts: 'employees.del.contracts',
      clientAccounts: 'employees.del.clientAccounts',
      driveItems: 'employees.del.driveItems',
      sites: 'employees.del.sites',
    },
  },
  {
    key: 'released',
    title: 'employees.deleteReleased',
    lines: {
      tasks: 'employees.del.openTasks',
      tickets: 'employees.del.openTickets',
      qTasks: 'employees.del.openQTasks',
    },
  },
];

/** What the page says when the API refused and changed nothing (`details.reason`). */
const REFUSED: Record<string, TranslationKey> = {
  SELF: 'employees.deleteSelf',
  LAST_SYS_ADMIN: 'employees.deleteLastSysAdmin',
  CONFIRMATION: 'employees.deleteConfirmMismatch',
  SUCCESSOR_REQUIRED: 'employees.deleteSuccessorRequired',
  SUCCESSOR_INVALID: 'employees.deleteSuccessorInvalid',
};

/**
 * Deleting an employee for good (operator, 2026-10-19: "let me delete employee
 * and all his records but only for admin"), at `/employees/:id/delete`. The page
 * says first what goes, what passes to someone else and what is left without
 * them; whoever takes over is picked when they own anything, and their email is
 * typed to confirm. Only `users.delete` — the Administrator's — reaches it.
 */
export function EmployeeDeletePage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmEmail, setConfirmEmail] = useState('');
  const [successorId, setSuccessorId] = useState('');

  const plan = useQuery({
    queryKey: ['user', id, 'deletion'],
    queryFn: () => api.get<DeletionPlan>(`/users/${id}/deletion`),
  });
  const data = plan.data;

  const { data: people } = useQuery({
    queryKey: ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options'),
    enabled: !!data?.needsSuccessor,
  });

  const remove = useMutation({
    mutationFn: () =>
      api.del(`/users/${id}`, { confirmEmail, ...(data?.needsSuccessor ? { successorId } : {}) }),
    onSuccess: () => {
      toast.success(t('employees.deleted', { name: data ? localized(data.employee) : '' }));
      queryClient.removeQueries({ queryKey: ['user', id] });
      // Tasks, tickets, projects and more changed with them: nothing cached is trusted.
      void queryClient.invalidateQueries();
      navigate('/employees', { replace: true });
    },
    onError: (e: any) => {
      const reason = REFUSED[e?.code === 'LAST_SYS_ADMIN' ? 'LAST_SYS_ADMIN' : (e?.details?.reason as string)];
      toast.error(reason ? t(reason) : e.message);
    },
  });

  const confirmed = !!data && confirmEmail.trim().toLowerCase() === data.employee.email.toLowerCase();
  const ready = !!data && !data.refusal && confirmed && (!data.needsSuccessor || !!successorId);
  const successors = (people ?? []).filter((p) => p.id !== id);

  return (
    <FormPage
      title={data ? t('employees.deleteTitle', { name: localized(data.employee) }) : t('employees.delete')}
      subtitle={t('employees.deleteHint')}
      backTo={`/employees/${id}`}
      backLabel={data ? localized(data.employee) : t('common.back')}
      submitLabel={t('employees.deleteSubmit')}
      destructive
      submitting={remove.isPending}
      disabled={!ready}
      loading={plan.isLoading}
      error={plan.error}
      onRetry={() => void plan.refetch()}
      width="lg"
      summary={
        data && (
          <div className="grid gap-5 sm:grid-cols-2">
            {GROUPS.map((group) => {
              const lines = Object.entries(group.lines).filter(([key]) => (data[group.key][key] ?? 0) > 0);
              return (
                <section key={group.key}>
                  <h3 className="mb-2 text-body-sm font-semibold text-[var(--text-primary)]">{t(group.title)}</h3>
                  {lines.length ? (
                    <dl className="divide-y divide-[var(--line)] text-body-sm">
                      {lines.map(([key, label]) => (
                        <div key={key} className="flex items-baseline justify-between gap-3 py-1.5">
                          <dt className="text-muted">{t(label)}</dt>
                          <dd className="tabular text-[var(--text-primary)]">
                            {formatNumber(data[group.key][key], locale)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="text-body-sm text-faint">{t('employees.deleteNothing')}</p>
                  )}
                </section>
              );
            })}
            <p className="text-caption text-muted sm:col-span-2">{t('employees.deleteKept')}</p>
          </div>
        )
      }
      onSubmit={() => remove.mutate()}
    >
      {data && (
        <div className="space-y-4">
          {data.refusal ? (
            <p className="rounded-md border border-error/35 bg-error-bg px-3 py-2 text-body-sm text-error-fg dark:bg-error/8 dark:text-error-dark">
              {t(REFUSED[data.refusal])}
            </p>
          ) : (
            <p className="rounded-md border border-error/35 bg-error-bg px-3 py-2 text-body-sm text-error-fg dark:bg-error/8 dark:text-error-dark">
              {t('employees.deleteWarning')}
            </p>
          )}

          {data.signedOffPayslips.length > 0 && (
            <p className="rounded-md border border-warning/35 bg-warning-bg px-3 py-2 text-body-sm text-warning-fg dark:bg-warning/8 dark:text-warning-dark">
              {t('employees.deleteSignedOffPayslips', {
                months: data.signedOffPayslips.map((p) => formatMonth(p.year, p.month, locale)).join(locale === 'ar' ? '، ' : ', '),
              })}
            </p>
          )}

          {data.needsSuccessor && (
            <Field label={t('employees.deleteSuccessor')} hint={t('employees.deleteSuccessorHint')} required>
              <Select value={successorId} onChange={(e) => setSuccessorId(e.target.value)} disabled={!!data.refusal}>
                <option value="">—</option>
                {successors.map((p) => (
                  <option key={p.id} value={p.id}>
                    {localized(p)}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field
            label={t('employees.deleteConfirm')}
            hint={
              <>
                {t('employees.deleteConfirmHint')}{' '}
                <span dir="ltr" className="font-mono text-mono-sm text-[var(--text-secondary)]">
                  {data.employee.email}
                </span>
              </>
            }
            required
          >
            <Input
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              dir="ltr"
              autoComplete="off"
              spellCheck={false}
              disabled={!!data.refusal}
            />
          </Field>
        </div>
      )}
    </FormPage>
  );
}
